import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createPortal } from "react-dom";
import { fetchUpdateProfile } from "../../Reducers/actions/userActions";
import { updateUser } from "../../Reducers/actions/authActions";
// import { useNavigate } from "react-router-dom";
import './EditNetworks.scss';
import { clearTopNavbar, setTopNavbar } from "../../Reducers/actions/navbarActions";

const formatSocialUrl = (platform, value) => {
    // Extract username from various URL formats or use the value directly
    let username = value;
    
    // If it's a full URL, extract the username part
    if (value.includes('/')) {
        // Remove protocol and www
        let cleanUrl = value.replace(/^(https?:\/\/)?(www\.)?/, '');
        
        // Extract username based on platform
        switch (platform) {
            case 'instagram':
                // Extract from instagram.com/username or just username
                username = cleanUrl.replace(/^instagram\.com\//, '');
                break;
            case 'twitter':
                // Extract from x.com/username, twitter.com/username, or just username
                username = cleanUrl.replace(/^(x\.com|twitter\.com)\//, '');
                break;
            case 'facebook':
                // Extract from facebook.com/username or just username
                username = cleanUrl.replace(/^facebook\.com\//, '');
                break;
            case 'youtube':
                // Extract from youtube.com/@username, youtube.com/channel/..., or just username
                username = cleanUrl.replace(/^youtube\.com\/(@|channel\/|c\/|user\/)?/, '');
                break;
            default:
                username = cleanUrl;
        }
    }

    // Platform-specific formatting
    switch (platform) {
        case 'instagram':
            return `https://instagram.com/${username}`;
        case 'twitter':
            return `https://x.com/${username}`;
        case 'facebook':
            return `https://facebook.com/${username}`;
        case 'youtube':
            // Handle both channel and user URLs
            if (username.startsWith('channel/') || username.startsWith('c/') || username.startsWith('user/')) {
                return `https://youtube.com/${username}`;
            }
            return `https://youtube.com/@${username}`;
        default:
            return username;
    }
};

const validateSocialUrl = (platform, value) => {
    const patterns = {
        instagram: /^[a-zA-Z0-9._]+$/,
        twitter: /^[a-zA-Z0-9_]+$/,
        facebook: /^[a-zA-Z0-9.]+$/,
        youtube: /^[a-zA-Z0-9_-]+$/
    };

    return patterns[platform].test(value);
};

const SOCIALS = [
    {
        key: "instagram",
        label: "Instagram",
        placeholder: "Add your Instagram username",
        icon: "/social-icons/instagram.svg",
    },
    {
        key: "twitter",
        label: "X",
        placeholder: "Add your X username",
        icon: "/social-icons/x.svg",
    },
    {
        key: "facebook",
        label: "Facebook",
        placeholder: "Add your Facebook username",
        icon: "/social-icons/facebook.svg",
    },
    {
        key: "youtube",
        label: "Youtube",
        placeholder: "Add your Youtube username",
        icon: "/social-icons/youtube.svg",
    },
];

const EditNetworks = () => {
    const user = useSelector((state) => state.auth.user || {});
    const dispatch = useDispatch();
    // const navigate = useNavigate();

    const [links, setLinks] = useState({
        instagram: user.instagram || "",
        twitter: user.twitter || "",
        facebook: user.facebook || "",
        youtube: user.youtube || "",
    });
    const [rawInputs, setRawInputs] = useState({
        instagram: user.instagram ? formatSocialUrl('instagram', user.instagram).replace(/^https:\/\/instagram\.com\//, '') : "",
        twitter: user.twitter ? formatSocialUrl('twitter', user.twitter).replace(/^https:\/\/x\.com\//, '') : "",
        facebook: user.facebook ? formatSocialUrl('facebook', user.facebook).replace(/^https:\/\/facebook\.com\//, '') : "",
        youtube: user.youtube ? formatSocialUrl('youtube', user.youtube).replace(/^https:\/\/youtube\.com\/(@|channel\/|c\/|user\/)?/, '') : "",
    });
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(false);
    const [changed, setChanged] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        dispatch(setTopNavbar({
            title: "Edit Networks",
            showBackArrow: true,
        }));

        return () => {
            dispatch(clearTopNavbar());
        };
    }, []);

    const handleEdit = (key) => setEditing(key);

    const handleChange = (key, value) => {
        setRawInputs(prev => ({ ...prev, [key]: value }));
        const isValid = validateSocialUrl(key, value);
        setValidationErrors(prev => ({ ...prev, [key]: !isValid }));
        setChanged(true);
    };

    const handleBlur = (key) => {
        const formattedUrl = formatSocialUrl(key, rawInputs[key]);
        setLinks(prev => ({ ...prev, [key]: formattedUrl }));
        setEditing(null);
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (loading) return; // Prevent multiple saves

        setLoading(true);
        setError(null);

        // Format all URLs before saving, ensuring empty strings for null/undefined/empty values
        const formattedLinks = Object.keys(links).reduce((acc, key) => {
            // If the input is null, undefined, or empty string, save as empty string
            if (!rawInputs[key] || rawInputs[key].trim() === '') {
                acc[key] = '';
            } else {
                acc[key] = formatSocialUrl(key, rawInputs[key].trim());
            }
            return acc;
        }, {});

        // Only include the social media fields we want to update
        const updatedUser = {
            user_id: user.user_id,
            instagram: formattedLinks.instagram,
            twitter: formattedLinks.twitter,
            facebook: formattedLinks.facebook,
            youtube: formattedLinks.youtube
        };



        fetchUpdateProfile(updatedUser, user.user_id)
            // .then(({ success, message, user: resUser }) => {
            .then(({ success, message }) => {

                if (success) {
                    // Update the local state first
                    setLinks(formattedLinks);
                    setRawInputs(prev => {
                        const newRawInputs = {};
                        Object.keys(prev).forEach(key => {
                            if (formattedLinks[key]) {
                                switch (key) {
                                    case 'instagram':
                                        newRawInputs[key] = formattedLinks[key].replace(/^https:\/\/instagram\.com\//, '');
                                        break;
                                    case 'twitter':
                                        newRawInputs[key] = formattedLinks[key].replace(/^https:\/\/x\.com\//, '');
                                        break;
                                    case 'facebook':
                                        newRawInputs[key] = formattedLinks[key].replace(/^https:\/\/facebook\.com\//, '');
                                        break;
                                    case 'youtube':
                                        newRawInputs[key] = formattedLinks[key].replace(/^https:\/\/youtube\.com\/(@|channel\/|c\/|user\/)?/, '');
                                        break;
                                    default:
                                        newRawInputs[key] = '';
                                }
                            } else {
                                newRawInputs[key] = '';
                            }
                        });
                        return newRawInputs;
                    });

                    // Then update Redux with the full user object
                    dispatch(updateUser({ ...user, ...formattedLinks }));
                    setChanged(false);
                } else {
                    setError(message || "Failed to update networks");
                }
            })
            .catch(err => {
                console.error("Save error details:", {
                    error: err,
                    userData: updatedUser,
                    userId: user.user_id
                });
                setError("An error occurred while saving. Please try again.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <>
            {/* Render button using portal to isolate it from parent CSS */}
        
                <button
                    className={`btn-tertiary ${changed && !loading ? 'active' : ''} `}
                    style={{ 
                        position: 'fixed',
                        top: '20px', 
                        right: '16px',
                        zIndex: 1000,
                        width: '80px',
                        minWidth: '80px'
                    }}
                    disabled={!changed || loading}
                    onClick={handleSave}
                    type="button"
                >
                    {loading ? "Saving..." : "Save"}
                </button>
            
            <div className="d-flex f-column aic p-16 mt-40 pt-32" style={{ background: "#fff", minHeight: "100dvh" }}>
                <form className="w-full" onSubmit={handleSave}>
                {SOCIALS.map(({ key, placeholder, icon }) => (
                    <div key={key} className="mb-16">
                        <div
                            className="d-flex aic w-full"
                            style={{ borderBottom: editing === key ? "1px solid #FF3232" : "1px solid #eee", padding: "12px 0" }}
                            onClick={() => handleEdit(key)}
                        >
                            <img
                                src={icon}
                                alt={key}
                                width={24}
                                height={24}
                                className="mr-12"
                            />
                            {editing === key ? (
                                <input
                                    type="text"
                                    value={rawInputs[key]}
                                    onChange={e => handleChange(key, e.target.value)}
                                    placeholder={placeholder}
                                    className={`ml-12 input-social ${validationErrors[key] ? 'invalid' : 'valid'}`}
                                    onBlur={() => handleBlur(key)}
                                    style={{ flex: 1, minHeight: 24, border: "none", outline: "none" }}
                                />
                            ) : (
                                <span className="ml-12 fg-grey fs-xs" style={{ flex: 1, minHeight: 24 }}>
                                    {links[key] ? (
                                        <span className="fg-crea">{links[key]}</span>
                                    ) : (
                                        <span className="fg-grey">{placeholder}</span>
                                    )}
                                </span>
                            )}
                        </div>
                        {/* Preview and Error messages below the field - matching Figma design */}
                        {editing === key && rawInputs[key] && !validationErrors[key] && (
                            <div className="field-preview">
                                <span className="preview-label">Preview:</span>
                                <a href={formatSocialUrl(key, rawInputs[key])} target="_blank" rel="noopener noreferrer">
                                    {formatSocialUrl(key, rawInputs[key])}
                                </a>
                            </div>
                        )}
                        {editing === key && validationErrors[key] && (
                            <div className="field-error">
                                Please enter a valid {key === 'twitter' ? 'X' : key.charAt(0).toUpperCase() + key.slice(1)} username
                            </div>
                        )}
                    </div>
                ))}
                {error && <p className="fg-red mt-8 mb-0 fs-xs">{error}</p>}
            </form>
            </div>
        </>
    );
};

export default EditNetworks;